/**
 * EO/DAE engine rollout flag (roadmap Int-W2, the staged BoP→EO cutover).
 *
 * `VITE_EO_ENGINE` is a TRI-STATE build-time flag (the project's other flags are boolean, so this
 * gets its own parser) controlling how the deployed EO sim service (`eo-sim` Cloud Run) participates
 * in the canvas steady-state solve + report evidence:
 *
 *   - 'on'     (DEFAULT — the post-cutover flip; unset/invalid also resolves here) — EO results are
 *                surfaced (EO card + report-evidence block) and the canvas RUN routes to EO when the
 *                drawn graph is well-posed.
 *   - 'shadow' — EO solves are fired for soak/compare but results are NEVER shown or persisted.
 *   - 'off'    — EO is dormant: no EO surfaces are shown and the canvas RUN remains blocked. There
 *                is no alternate simulation engine or playback fallback.
 *
 * HISTORY: 'off' was the pre-cutover default; the default flipped to 'on' when the BoP→EO cutover
 * landed (#42, BoP retired). This doc previously still claimed 'off' — fixed (D-F7).
 *
 * Build-time (Vite): written by `cloudbuild.yaml` `_EO_ENGINE` → Dockerfile `ARG VITE_EO_ENGINE`
 * → the Vite build env, exactly like `_DASHBOARD_V2`/`_DAM_*`. Reads `import.meta.env` first
 * (browser bundle) then `process.env` (Node/tests), mirroring `runtimeFlags.ts:readEnvFlag`.
 */
export type EoEngineMode = 'off' | 'shadow' | 'on';

function readEnvFlag(name: string): string | undefined {
  // Test runners own their environment explicitly. Vite bakes computed-key
  // `import.meta.env` reads into each transformed module, so a developer's
  // release-like `.env.local` otherwise leaks into Vitest and defeats per-test
  // flag setup. Production and development bundles keep import.meta-first
  // precedence. Ported from `runtimeFlags.ts:readEnvFlag`, which this function
  // already CLAIMED to mirror but had drifted from.
  if (typeof process !== 'undefined'
    && process.env
    && (process.env.VITEST === 'true' || process.env.NODE_ENV === 'test')) {
    return process.env[name];
  }
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: unknown }).env != null) {
      const v = ((import.meta as { env: Record<string, unknown> }).env)[name];
      if (typeof v === 'string') return v;
    }
  } catch {
    /* import.meta unavailable in older Node — fall through */
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[name];
  }
  return undefined;
}

export function getEoEngineMode(): EoEngineMode {
  const raw = readEnvFlag('VITE_EO_ENGINE');
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  if (v === 'off') return 'off';
  if (v === 'shadow') return 'shadow';
  return 'on'; // default flip
}

/** True when EO should run at all (shadow OR on) — the gate for firing a solve against eo-sim. */
export function isEoEngineActive(): boolean {
  return getEoEngineMode() !== 'off';
}

/** True only when EO results should be SURFACED to the user (the 'on' flip, post-soak). */
export function isEoEngineSurfaced(): boolean {
  return getEoEngineMode() === 'on';
}
